// Multi-account registry.
//
// Layered ON TOP of the existing single-session keys so it's fully backwards
// compatible: the "active" account is still whatever lives in `authToken` /
// `refreshToken` / `authUser` (read by apiClient on every request and by
// accountSlice on load). This module adds a `dwlf.accounts` map of every
// signed-in account's session so a user can hold several at once and switch
// between them. An already-logged-in user with no registry yet is transparently
// seeded as a one-account set the first time the registry is read — nobody gets
// logged out.
//
// Switching does a hard navigation (window.location) rather than a soft Redux
// swap: it guarantees a clean re-bootstrap as the target account and sidesteps
// the in-flight-token-refresh race and any per-account cached state.

const REGISTRY_KEY = 'dwlf.accounts';
const ACTIVE_PATH = '/today';
const LOGIN_PATH = '/login';

const parseJSON = (raw, fallback) => {
  if (!raw || raw === 'undefined') return fallback;
  try {
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
};

const readActiveSession = () => ({
  token: localStorage.getItem('authToken') || null,
  refreshToken: localStorage.getItem('refreshToken') || null,
  user: parseJSON(localStorage.getItem('authUser'), null),
});

const readRegistry = () => {
  const parsed = parseJSON(localStorage.getItem(REGISTRY_KEY), {});
  return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
};

const writeRegistry = (reg) => localStorage.setItem(REGISTRY_KEY, JSON.stringify(reg));

const writeActiveSession = ({ token, refreshToken, user }) => {
  if (token) localStorage.setItem('authToken', token);
  else localStorage.removeItem('authToken');
  if (refreshToken) localStorage.setItem('refreshToken', refreshToken);
  else localStorage.removeItem('refreshToken');
  if (user) localStorage.setItem('authUser', JSON.stringify(user));
  else localStorage.removeItem('authUser');
};

/** Add/update an account's stored session in the registry. */
export const upsertAccount = ({ user, token, refreshToken }) => {
  if (!user?.accountId) return;
  const reg = readRegistry();
  reg[user.accountId] = { accountId: user.accountId, user, token, refreshToken };
  writeRegistry(reg);
};

/** Persist the current active session into the registry (keeps its tokens fresh
 *  — e.g. after a refresh rotation — before we switch away from it). */
export const snapshotActiveAccount = () => {
  const { token, refreshToken, user } = readActiveSession();
  if (user?.accountId && token) {
    upsertAccount({ user, token, refreshToken });
  }
};

export const getActiveAccountId = () => readActiveSession().user?.accountId || null;

/** Accounts for the menu (no tokens). Pure read — no localStorage writes, so it's
 *  safe to call during render. The active session is always folded into the
 *  result (on a copy, not persisted) so an already-logged-in user whose session
 *  predates the registry still sees their account. Persisting the active account
 *  is handled by snapshotActiveAccount() on menu open / before a switch. */
export const listAccounts = () => {
  const reg = { ...readRegistry() };
  const { user } = readActiveSession();
  const activeId = user?.accountId || null;
  if (activeId && !reg[activeId]) {
    reg[activeId] = { accountId: activeId, user };
  }
  return Object.values(reg)
    .filter((a) => a && a.accountId)
    .map((a) => ({
      accountId: a.accountId,
      email: a.user?.email || '',
      isActive: a.accountId === activeId,
    }));
};

/** Switch the active session to a stored account and hard-reload. */
export const switchAccount = (accountId) => {
  const reg = readRegistry();
  const target = reg[accountId];
  if (!target || accountId === getActiveAccountId()) return false;
  snapshotActiveAccount(); // keep the outgoing account's tokens current
  writeActiveSession(target);
  window.location.assign(ACTIVE_PATH);
  return true;
};

/** Remove the ACTIVE account; if others remain, switch to one (reload), else
 *  fully sign out to the login screen. Returns true if another account is now
 *  active, false if no accounts remain. */
export const signOutActiveAccount = () => {
  const activeId = getActiveAccountId();
  const reg = readRegistry();
  if (activeId) {
    delete reg[activeId];
    clearAccountScoped(activeId);
  }
  writeRegistry(reg);
  const remaining = Object.values(reg);
  if (remaining.length > 0) {
    // Fall back to the first remaining account (registry insertion order).
    // Deliberately simple — not "most-recently-used"; the user can switch from
    // there if it's not the one they wanted.
    writeActiveSession(remaining[0]);
    window.location.assign(ACTIVE_PATH);
    return true;
  }
  clearAll();
  window.location.assign(LOGIN_PATH);
  return false;
};

/** localStorage key for state that belongs to one account (e.g. the last chart opened). Sign-out and
 *  account removal clear these, so no account inherits another's — the charter's RESET_STATE rule for
 *  storage that outlives the store. Device preferences (the theme) are not account-scoped. */
export const accountScopedKey = (name, accountId = getActiveAccountId()) => `dwlf-acct:${accountId || 'anon'}:${name}`;

// In-memory caches of account data (short-TTL market reads) register a clearer here, so sign-out and
// account removal drop them with the stored keys rather than leaving them for RESET_STATE to miss.
const memoryClearers = new Set();
export const onAccountScopedClear = (clear) => { memoryClearers.add(clear); };

/** Remove account-scoped keys: one account's, or every account's when no id is given. */
export const clearAccountScoped = (accountId) => {
  const prefix = accountId ? `dwlf-acct:${accountId}:` : 'dwlf-acct:';
  Object.keys(localStorage).filter((k) => k.startsWith(prefix)).forEach((k) => localStorage.removeItem(k));
  memoryClearers.forEach((clear) => clear());
};

/** Remove the whole multi-account registry (manual full sign-out). */
export const clearRegistry = () => {
  localStorage.removeItem(REGISTRY_KEY);
  clearAccountScoped();
};

/** Remove a single account from the registry, leaving the others intact. Used by
 *  the involuntary expired-session logout so one stale account doesn't evict
 *  other (possibly still-valid) accounts. */
export const removeAccountFromRegistry = (accountId) => {
  if (!accountId) return;
  clearAccountScoped(accountId);
  const reg = readRegistry();
  if (reg[accountId]) {
    delete reg[accountId];
    writeRegistry(reg);
  }
};

/** Drop every stored account and the active session. */
export const clearAll = () => {
  localStorage.removeItem(REGISTRY_KEY);
  clearAccountScoped();
  localStorage.removeItem('authToken');
  localStorage.removeItem('authUser');
  localStorage.removeItem('refreshToken');
};

export const __TESTING__ = { REGISTRY_KEY, readRegistry, readActiveSession };
